// Eclipse geometry, shared by the viewer's shaders (as GLSL) and by code and
// tests (as JS). An eclipse is the Sun's disc covered by another disc: the
// Moon's, seen from a point on the Earth (a solar eclipse), or the Earth's,
// seen from a point on the Moon (a lunar eclipse). The fraction of the Sun's
// disc hidden is the fraction of its light lost, which gives the umbra (all
// of it), the penumbra (some) and the annular ring (all but a ring) from one
// formula with the real radii, so the shadows come out the size they are.
//
// The Moon's position for this has to be in the planets' frame: moon.js is
// ecliptic of date and Terrestrial Time, ephemeris.js (the Sun) is J2000 and
// the viewer's clock is UTC. Mixing them puts the Moon 0.4 degrees off by
// 2027, which moves the shadow thousands of kilometres and makes the
// 12 August 2026 eclipse miss the Earth. moonGeocentricJ2000() carries both
// corrections; with them the shadow axis passes the Earth's centre within
// 0.005 Earth radii of NASA's gamma (tests/eclipse-geometry.test.js).

import { moonEcliptic } from '../orbits/moon.js';
import { getPlanetPosition } from '../orbits/ephemeris.js';
import { EARTH_OBLIQUITY, earthRotationAngle } from '../orbits/earthframe.js';

export const R_EARTH_KM = 6371.0;
export const R_MOON_KM = 1737.4;
export const R_SUN_KM = 695700.0;
// The Earth's shadow on the Moon is wider than the solid Earth: the lower
// atmosphere is opaque to grazing sunlight. Danjon's rule adds 1/85 of the
// Earth's radius, the convention behind NASA's umbral magnitudes.
export const R_EARTH_SHADOW_KM = R_EARTH_KM * (1 + 1 / 85);
export const KM_PER_AU = 149597870.7;
// TT - UTC: 32.184 s plus the 37 leap seconds in force since 2017.
export const TT_MINUS_UTC_S = 69.184;

const ARCSEC = Math.PI / 648000;
const J2000 = 2451545.0;

// Ecliptic coordinates of date to J2000 (Meeus ch. 21, the rigorous ecliptic
// precession with the IAU 1976 constants). lon, lat in radians.
function eclipticOfDateToJ2000(lon, lat, jdTT) {
  const T = (jdTT - J2000) / 36525;
  const t = -T;
  const eta = ((47.0029 - 0.06603 * T + 0.000598 * T * T) * t + (-0.03302 + 0.000598 * T) * t * t + 0.00006 * t * t * t) * ARCSEC;
  const Pi = 174.876384 * Math.PI / 180 + (3289.4789 * T + 0.60622 * T * T - (869.8089 + 0.50491 * T) * t + 0.03536 * t * t) * ARCSEC;
  const p = ((5029.0966 + 2.22226 * T - 0.000042 * T * T) * t + (1.11113 - 0.000042 * T) * t * t - 0.000006 * t * t * t) * ARCSEC;
  const A = Math.cos(eta) * Math.cos(lat) * Math.sin(Pi - lon) - Math.sin(eta) * Math.sin(lat);
  const B = Math.cos(lat) * Math.cos(Pi - lon);
  const C = Math.cos(eta) * Math.sin(lat) + Math.sin(eta) * Math.cos(lat) * Math.sin(Pi - lon);
  return { lon: p + Pi - Math.atan2(A, B), lat: Math.asin(C) };
}

// The Moon's geocentric position, J2000 ecliptic, km, at a UTC Julian date.
export function moonGeocentricJ2000(jdUtc) {
  const jdTT = jdUtc + TT_MINUS_UTC_S / 86400;
  const m = moonEcliptic(jdTT);
  const { lon, lat } = eclipticOfDateToJ2000(m.lon, m.lat, jdTT);
  const cl = Math.cos(lat);
  return { x: m.km * cl * Math.cos(lon), y: m.km * cl * Math.sin(lon), z: m.km * Math.sin(lat), km: m.km };
}

// The Sun's geocentric position, J2000 ecliptic, km: the opposite of the
// Earth-Moon barycentre's heliocentric position, moved to the Earth's centre.
export function sunGeocentricJ2000(jdUtc) {
  const b = getPlanetPosition('earth', jdUtc + TT_MINUS_UTC_S / 86400);
  const m = moonGeocentricJ2000(jdUtc);
  const k = 1 / 82.30057; // Moon mass / (Earth + Moon mass): the Earth's offset from the barycentre
  return { x: -b.x * KM_PER_AU + m.x * k, y: -b.y * KM_PER_AU + m.y * k, z: -b.z * KM_PER_AU + m.z * k };
}

const sub = (a, b) => ({ x: a.x - b.x, y: a.y - b.y, z: a.z - b.z });
const dot = (a, b) => a.x * b.x + a.y * b.y + a.z * b.z;
const len = (a) => Math.sqrt(dot(a, a));
const cross = (a, b) => ({ x: a.y * b.z - a.z * b.y, y: a.z * b.x - a.x * b.z, z: a.x * b.y - a.y * b.x });

// The fraction of a disc of angular radius rs covered by a disc of radius ro
// whose centre is d away (small angles, so the sky is flat here).
export function discCover(rs, ro, d) {
  if (d >= rs + ro) return 0;
  if (d <= Math.abs(rs - ro)) return ro >= rs ? 1 : (ro * ro) / (rs * rs);
  const rs2 = rs * rs, ro2 = ro * ro, d2 = d * d;
  const a1 = rs2 * Math.acos(Math.min(1, Math.max(-1, (d2 + rs2 - ro2) / (2 * d * rs))));
  const a2 = ro2 * Math.acos(Math.min(1, Math.max(-1, (d2 + ro2 - rs2) / (2 * d * ro))));
  const a3 = 0.5 * Math.sqrt(Math.max(0, (-d + rs + ro) * (d + rs - ro) * (d - rs + ro) * (d + rs + ro)));
  return (a1 + a2 - a3) / (Math.PI * rs2);
}

// The fraction of the Sun hidden, seen from p, by a sphere of radius r at
// occ. sun, occ and p in km in any one frame.
export function sunHidden(p, sun, occ, r) {
  const s = sub(sun, p);
  const o = sub(occ, p);
  if (dot(s, o) <= 0) return 0;
  const ds = len(s), dO = len(o);
  const rs = Math.asin(Math.min(1, R_SUN_KM / ds));
  const ro = Math.asin(Math.min(1, r / dO));
  const d = Math.atan2(len(cross(s, o)), dot(s, o));
  return discCover(rs, ro, d);
}

// ---- Checks against NASA's tables --------------------------------------------

// gamma: how far the shadow axis (Sun to Moon) passes from the Earth's
// centre, in Earth radii, signed north (+) or south of the equatorial plane.
export function eclipseGamma(jdUtc) {
  const sun = sunGeocentricJ2000(jdUtc);
  const moon = moonGeocentricJ2000(jdUtc);
  const axis = sub(moon, sun);
  const u = { x: axis.x / len(axis), y: axis.y / len(axis), z: axis.z / len(axis) };
  const t = -dot(sun, u);
  const c = { x: sun.x + u.x * t, y: sun.y + u.y * t, z: sun.z + u.z * t };
  // North of the equator: the J2000 equatorial z, from the ecliptic axes.
  const zEq = Math.sin(EARTH_OBLIQUITY) * c.y + Math.cos(EARTH_OBLIQUITY) * c.z;
  return (len(c) / R_EARTH_KM) * Math.sign(zEq);
}

// Where the shadow axis meets the Earth (geodetic latitude ignored: the
// sphere the viewer draws), as { lat, lon } in degrees, east positive, using
// the viewer's own Earth rotation (earthframe.js). Null when it misses.
export function shadowAxisGround(jdUtc) {
  const sun = sunGeocentricJ2000(jdUtc);
  const moon = moonGeocentricJ2000(jdUtc);
  const axis = sub(moon, sun);
  const u = { x: axis.x / len(axis), y: axis.y / len(axis), z: axis.z / len(axis) };
  // Ray from the Moon along the axis: |moon + u t| = R.
  const b = dot(moon, u);
  const c = dot(moon, moon) - R_EARTH_KM * R_EARTH_KM;
  const disc = b * b - c;
  if (disc < 0) return null;
  const t = -b - Math.sqrt(disc);
  const g = { x: moon.x + u.x * t, y: moon.y + u.y * t, z: moon.z + u.z * t };
  // Ecliptic J2000 to equatorial J2000, then to the Earth's longitude.
  const e = EARTH_OBLIQUITY;
  const xq = g.x, yq = Math.cos(e) * g.y - Math.sin(e) * g.z, zq = Math.sin(e) * g.y + Math.cos(e) * g.z;
  const lat = Math.asin(zq / R_EARTH_KM) * 180 / Math.PI;
  let lon = (Math.atan2(yq, xq) - earthRotationAngle(jdUtc)) * 180 / Math.PI;
  lon = ((lon + 540) % 360) - 180;
  return { lat, lon };
}

// A lunar eclipse's umbral magnitude: the fraction of the Moon's diameter
// inside the umbra (more than 1 for a total eclipse), as NASA quotes it.
export function lunarUmbralMagnitude(jdUtc) {
  const sun = sunGeocentricJ2000(jdUtc);
  const moon = moonGeocentricJ2000(jdUtc);
  const dm = len(moon), dsun = len(sun);
  const piM = Math.asin(R_EARTH_KM / dm);
  const piS = Math.asin(R_EARTH_KM / dsun);
  const sS = Math.asin(R_SUN_KM / dsun);
  const sM = Math.asin(R_MOON_KM / dm);
  const umbra = (1 + 1 / 85) * piM - sS + piS;
  const anti = { x: -sun.x, y: -sun.y, z: -sun.z };
  const d = Math.atan2(len(cross(anti, moon)), dot(anti, moon));
  return (umbra + sM - d) / (2 * sM);
}

// ---- The same in GLSL -----------------------------------------------------------
// eclSunHidden(p, sun, occ, r) as above; eclSunHiddenDepth also returns how
// deep inside the umbra p is (0 at its edge, 1 on its axis), for the colour
// of a lunar eclipse. Separations come from atan(|a x b|, a . b), which keeps
// its precision at the 0.27 degree sizes involved where acos(dot) does not.

export const ECLIPSE_GLSL = /* glsl */ `
const float ECL_R_EARTH = ${R_EARTH_KM.toFixed(1)};
const float ECL_R_MOON = ${R_MOON_KM.toFixed(1)};
const float ECL_R_SUN = ${R_SUN_KM.toFixed(1)};
const float ECL_R_EARTH_SHADOW = ${R_EARTH_SHADOW_KM.toFixed(2)};

float eclDiscCover(float rs, float ro, float d) {
  if (d >= rs + ro) return 0.0;
  if (d <= abs(rs - ro)) return ro >= rs ? 1.0 : (ro * ro) / (rs * rs);
  float rs2 = rs * rs;
  float ro2 = ro * ro;
  float d2 = d * d;
  float a1 = rs2 * acos(clamp((d2 + rs2 - ro2) / (2.0 * d * rs), -1.0, 1.0));
  float a2 = ro2 * acos(clamp((d2 + ro2 - rs2) / (2.0 * d * ro), -1.0, 1.0));
  float a3 = 0.5 * sqrt(max(0.0, (-d + rs + ro) * (d + rs - ro) * (d - rs + ro) * (d + rs + ro)));
  return (a1 + a2 - a3) / (3.14159265 * rs2);
}

vec2 eclSunHiddenDepth(vec3 p, vec3 sun, vec3 occ, float r) {
  vec3 s = sun - p;
  vec3 o = occ - p;
  if (dot(s, o) <= 0.0) return vec2(0.0);
  float ds = length(s);
  float dO = length(o);
  vec3 sn = s / ds;
  vec3 on = o / dO;
  float rs = asin(min(1.0, ECL_R_SUN / ds));
  float ro = asin(min(1.0, r / dO));
  float d = atan(length(cross(sn, on)), dot(sn, on));
  float depth = ro > rs ? clamp(((ro - rs) - d) / (ro - rs), 0.0, 1.0) : 0.0;
  return vec2(eclDiscCover(rs, ro, d), depth);
}

float eclSunHidden(vec3 p, vec3 sun, vec3 occ, float r) {
  return eclSunHiddenDepth(p, sun, occ, r).x;
}
`;
